'use client'

import { Badge, Button, Card, CardContent, Download, EyeIcon } from '@chain-zen/ui'
import { FileIcon } from 'react-file-utils'

import type { IShipmentFile } from '../../../../../types/shipment'
import {
  formatTimestamp,
  getDisplayFileName,
  getExtractedDataEntries,
  getMimeTypeFromFileName,
  truncateText
} from './files-details.utils'

interface ShipmentFileListItemProps {
  file: IShipmentFile
  onDownload: (url: string) => void
  onPreview: (file: IShipmentFile) => void
}

export const ShipmentFileListItem = ({ file, onDownload, onPreview }: ShipmentFileListItemProps) => {
  const fileName = getDisplayFileName(file.url.split('/').pop()?.split('?')[0] || 'File')
  const fileTimestamp = formatTimestamp(file.timestamp)
  const extractedEntries = getExtractedDataEntries(file)

  return (
    <Card className="gap-0 rounded-xl py-0">
      <CardContent className="flex items-start gap-3 px-3 py-3 sm:px-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border bg-muted/10 text-muted-foreground">
          <FileIcon filename={fileName} mimeType={getMimeTypeFromFileName(fileName)} size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1 space-y-2">
              <Badge variant="secondary" className="max-w-full truncate">
                {file.classificationType}
              </Badge>
              <p title={fileName} className="truncate text-sm font-medium text-foreground">
                {truncateText(fileName, 40)}
              </p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span>{file.source}</span>
                {fileTimestamp !== null ? <span>{fileTimestamp}</span> : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Preview ${file.classificationType} ${fileName}`}
                className="size-8 rounded-lg text-muted-foreground"
                onClick={() => onPreview(file)}
              >
                <EyeIcon className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Download ${file.classificationType} ${fileName}`}
                className="size-8 rounded-lg text-muted-foreground"
                onClick={() => {
                  void onDownload(file.url)
                }}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {extractedEntries.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {extractedEntries.slice(0, 2).map((entry) => (
                <Badge
                  key={`${entry.label}-${entry.value}`}
                  variant="outline"
                  className="max-w-full truncate border-border/70 bg-muted/20 text-xs font-normal text-muted-foreground"
                  title={`${entry.label}: ${entry.value}`}
                >
                  {entry.label}: {truncateText(entry.value)}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
